import React, { useState, useEffect } from "react";
import {
  FileText,
  Link as LinkIcon,
  Users,
  XCircle,
  Activity,
  AlertCircle,
  RotateCcw,
} from "lucide-react";
import Button from "../../../components/common/ui/button/Button";
import type { Case, CaseStatus } from "../../../types";
import type { AuditLogEntry } from "../../../types/audit";
import { apiClient } from "../../../utils/apiClient";
import { useOrg } from "../../../contexts/OrgContext";
import AssignUserModal from "./AssignUserModal";
import ChangePriorityModal from "./ChangePriorityModal";
import ActivityLogItem from "../../../components/common/activity/ActivityLogItem";

interface OverviewTabProps {
  caseData: Case;
  fileCount: number;
  formSubmissionCount: number;
  pendingRequestCount: number;
  lastFileUpload?: string | null;
  caseId: string;
  onSwitchToActivityTab?: () => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  caseData,
  fileCount,
  formSubmissionCount,
  pendingRequestCount,
  lastFileUpload,
  caseId,
  onSwitchToActivityTab,
}) => {
  const { navigateOrg } = useOrg();
  const [recentActivity, setRecentActivity] = useState<AuditLogEntry[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isPriorityModalOpen, setIsPriorityModalOpen] = useState(false);

  useEffect(() => {
    loadRecentActivity();
  }, [caseId]);

  const loadRecentActivity = async () => {
    setLoadingActivity(true);
    try {
      const response = await apiClient.getAuditLogs({
        caseId,
        limit: 5,
        page: 1,
      });
      setRecentActivity(response.logs);
    } catch (error) {
      console.error("Failed to load recent activity:", error);
      setRecentActivity([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const formatTimeAgo = (dateString?: string | null) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  };

  const handleCloseCase = async () => {
    if (!confirm("Are you sure you want to close this case?")) return;

    try {
      await apiClient.updateCase(caseId, { status: "closed" as CaseStatus });
      window.location.reload();
    } catch (error) {
      console.error("Failed to close case:", error);
      alert("Failed to close case");
    }
  };

  const handleReopenCase = async () => {
    if (!confirm("Are you sure you want to reopen this case?")) return;

    try {
      await apiClient.updateCase(caseId, { status: "open" as CaseStatus });
      window.location.reload();
    } catch (error) {
      console.error("Failed to reopen case:", error);
      alert("Failed to reopen case");
    }
  };

  const isCaseClosed = caseData.status === "closed";

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-3 dark:bg-blue-900/20">
              <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-black dark:text-white">
                {fileCount}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Files</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-100 p-3 dark:bg-green-900/20">
              <FileText className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-black dark:text-white">
                {formSubmissionCount}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Form Submissions
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-orange-100 p-3 dark:bg-orange-900/20">
              <LinkIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-black dark:text-white">
                {pendingRequestCount}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Pending Requests
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-100 p-3 dark:bg-purple-900/20">
              <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-black dark:text-white">
                {formatTimeAgo(lastFileUpload)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Last File Upload
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <h3 className="mb-4 text-lg font-semibold text-black dark:text-white">
          Quick Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="primary"
            onClick={() => navigateOrg(`/cases/${caseData.id}/requests/new`)}
            startIcon={<LinkIcon size={18} />}
          >
            Make a Request
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsAssignModalOpen(true)}
            startIcon={<Users size={18} />}
          >
            Assign Users
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsPriorityModalOpen(true)}
            startIcon={<AlertCircle size={18} />}
          >
            Change Priority
          </Button>
          {isCaseClosed ? (
            <Button
              variant="outline"
              onClick={handleReopenCase}
              startIcon={<RotateCcw size={18} />}
            >
              Reopen Case
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handleCloseCase}
              startIcon={<XCircle size={18} />}
            >
              Close Case
            </Button>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-black dark:text-white">
            Recent Activity
          </h3>
          {onSwitchToActivityTab && (
            <Button variant="outline" size="sm" onClick={onSwitchToActivityTab}>
              View All Activity
            </Button>
          )}
        </div>

        {loadingActivity ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading activity...
            </p>
          </div>
        ) : recentActivity.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Activity
              size={48}
              className="mb-2 text-gray-300 dark:text-gray-700"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No recent activity
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentActivity.slice(0, 5).map((entry) => (
              <ActivityLogItem key={entry.id} entry={entry} variant="minimal" />
            ))}
          </div>
        )}
      </div>

      {/* Assign User Modal */}
      <AssignUserModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        caseId={caseId}
        organizationId={caseData.organizationId}
        onSuccess={loadRecentActivity}
      />

      {/* Change Priority Modal */}
      <ChangePriorityModal
        isOpen={isPriorityModalOpen}
        onClose={() => setIsPriorityModalOpen(false)}
        caseId={caseId}
        currentPriority={caseData.priority}
        onSuccess={() => {
          loadRecentActivity();
          window.location.reload();
        }}
      />
    </div>
  );
};

export default OverviewTab;
